// Excel „Rollennachweis“ (Rollen, Kundenwunsch 27.10.2026): Aufstellung über den Verbrauch der Thermorollen und
// Papierabschnitte, wie sie der MVV von Unternehmen mit eigenem Bordrechnersystem verlangen kann (Handlungsanweisung
// Fahrkarten, Anlage A 10, Abschnitt 6). Blätter: Rollen (je Rolle der letzten 30 Tage, mit Einlage), Lücken (mit
// Erklärung), Drucke (jeder Verkauf und jeder Druck ohne Verkauf – Testdruck, Abrechnung … seit 05.11.2026 – mit seinen
// Abschnitten) und Info (Mandant, Zeitraum, Kapazität, Stand).
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import moment from 'moment';
import { rangeText, printText, insertedText } from './rollTracking';

// Ortszeit-Text als Excel-Datum: exceljs rechnet in UTC, daher die Ortszeit als UTC-Zeitpunkt übergeben (sonst
// verschiebt sich die Uhrzeit um den Zeitzonenversatz)
function excelDate(text) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(String(text || ''));
  return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0))) : null;
}

function statusText(roll) {
  if (roll.status === 'active') {
    return 'im Einsatz';
  }
  return roll.full ? 'vollständig verbraucht' : 'beendet bei Abschnitt ' + roll.max;
}

function addSheet(workbook, name, columns, rows) {
  const sheet = workbook.addWorksheet(name, { views: [{ state: 'frozen', ySplit: 1 }] });
  sheet.columns = columns.map((c) => ({ header: c.header, key: c.key, width: c.width || 14, style: c.style || {} }));
  sheet.getRow(1).font = { bold: true };
  rows.forEach((row) => sheet.addRow(row));
  sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: columns.length } };
  return sheet;
}

const DATE_STYLE = { numFmt: 'dd.mm.yyyy hh:mm' };

export async function exportRollReport(result, { customerName = '', printerNames = {}, now = new Date() } = {}) {
  const rolls = result.active.concat(result.finished);
  const printerText = (p) => 'Drucker ' + p + (printerNames[p] ? ' (' + printerNames[p] + ')' : '');
  const workbook = new Workbook();

  addSheet(workbook, 'Rollen', [
    { header: 'Rolle', key: 'roll', width: 10 },
    { header: 'Status', key: 'status', width: 26 },
    { header: 'Drucker', key: 'printers', width: 34 },
    { header: 'Abschnitt von', key: 'min', width: 14 },
    { header: 'Abschnitt bis', key: 'max', width: 14 },
    { header: 'Belegte Abschnitte', key: 'covered', width: 18 },
    { header: 'Rest (Abschnitte)', key: 'rest', width: 17 },
    { header: 'Unerklärte Lücken', key: 'gapsOpen', width: 18 },
    { header: 'Abschnitte in unerklärten Lücken', key: 'gapSections', width: 30 },
    { header: 'Erklärte Lücken', key: 'gapsExplained', width: 16 },
    { header: 'Unkritische Lücken (1 Abschnitt)', key: 'gapsMinor', width: 30 },
    { header: 'Verkäufe', key: 'sales', width: 10 },
    { header: 'Drucke ohne Verkauf', key: 'prints', width: 19 },
    { header: 'Eingelegt', key: 'inserted', width: 36 },
    { header: 'Eingelegt am', key: 'insertedAt', width: 17, style: DATE_STYLE },
    { header: 'Erster Druck', key: 'first', width: 17, style: DATE_STYLE },
    { header: 'Letzter Druck', key: 'last', width: 17, style: DATE_STYLE },
    { header: 'Hinweise', key: 'notes', width: 60 }
  ], rolls.map((r) => ({
    roll: r.id,
    status: statusText(r),
    printers: r.printers.map(printerText).join(', '),
    min: r.min,
    max: r.max,
    covered: r.covered,
    rest: r.status === 'active' ? r.rest : null,
    gapsOpen: r.unexplained.length,
    gapSections: r.unexplainedSections,
    gapsExplained: r.gaps.filter((g) => g.explained).length,
    gapsMinor: r.minorGaps.length,
    sales: r.saleCount,
    prints: r.printCount || 0,
    inserted: r.inserted ? insertedText(r.inserted) + ', ' + printerText(r.inserted.printer) : '',
    insertedAt: r.inserted ? excelDate(r.inserted.time) : null,
    first: excelDate(r.firstTime),
    last: excelDate(r.lastTime),
    notes: r.warnings.map((w) => w.text).join('; ')
  })));

  const gapRows = [];
  rolls.forEach((r) => r.gaps.forEach((g) => gapRows.push({
    roll: r.id,
    sections: rangeText(g.from, g.to),
    count: g.count,
    rating: g.explained ? 'erklärt' : g.minor ? 'unkritisch (1 Abschnitt)' : 'unerklärt',
    reason: g.reason,
    beforeTime: g.before ? excelDate(g.before.time) : null,
    beforePrinter: g.before ? g.before.printer : null,
    beforeSections: g.before ? rangeText(g.before.min, g.before.max) : '',
    afterTime: g.after ? excelDate(g.after.time) : null,
    afterPrinter: g.after ? g.after.printer : null,
    afterSections: g.after ? rangeText(g.after.min, g.after.max) : ''
  })));
  addSheet(workbook, 'Lücken', [
    { header: 'Rolle', key: 'roll', width: 10 },
    { header: 'Abschnitte', key: 'sections', width: 12 },
    { header: 'Anzahl', key: 'count', width: 9 },
    { header: 'Bewertung', key: 'rating', width: 24 },
    { header: 'Begründung', key: 'reason', width: 40 },
    { header: 'Druck davor', key: 'beforeTime', width: 17, style: DATE_STYLE },
    { header: 'Drucker davor', key: 'beforePrinter', width: 14 },
    { header: 'Abschnitte davor', key: 'beforeSections', width: 16 },
    { header: 'Druck danach', key: 'afterTime', width: 17, style: DATE_STYLE },
    { header: 'Drucker danach', key: 'afterPrinter', width: 15 },
    { header: 'Abschnitte danach', key: 'afterSections', width: 17 }
  ], gapRows);

  // Verkäufe und Drucke ohne Verkauf (e.sale bzw. e.print)
  const printRows = [];
  rolls.forEach((r) => r.events.forEach((e) => {
    const source = e.sale || e.print;
    printRows.push({
      roll: r.id,
      sections: rangeText(e.min, e.max),
      time: excelDate(e.time),
      printer: e.printer,
      kind: e.sale ? 'Verkauf' : 'ohne Verkauf',
      driver: source.username || '',
      ticket: e.sale ? e.sale.ticket_name || '' : printText(e.print),
      storno: e.sale && e.sale.is_cancellation ? 'Storno' : '',
      serial: source.printer_serial || ''
    });
  }));
  addSheet(workbook, 'Drucke', [
    { header: 'Rolle', key: 'roll', width: 10 },
    { header: 'Abschnitte', key: 'sections', width: 12 },
    { header: 'Druckzeit', key: 'time', width: 17, style: DATE_STYLE },
    { header: 'Drucker', key: 'printer', width: 10 },
    { header: 'Art', key: 'kind', width: 13 },
    { header: 'Fahrer', key: 'driver', width: 14 },
    { header: 'Ticket bzw. Druck', key: 'ticket', width: 32 },
    { header: 'Storno', key: 'storno', width: 9 },
    { header: 'Seriennummer Drucker', key: 'serial', width: 22 }
  ], printRows);

  const info = workbook.addWorksheet('Info');
  info.columns = [{ width: 28 }, { width: 60 }];
  [
    ['Rollennachweis', customerName],
    ['Stand', moment(now).format('DD.MM.YYYY HH:mm')],
    ['Zeitraum', 'Rollen mit Verkäufen der letzten ' + result.windowDays + ' Tage (Verlauf bis 120 Tage zurück)'],
    ['Kapazität je Rolle', result.capacity.value + ' Abschnitte (' + (result.capacity.learned
      ? 'gelernt aus ' + result.capacity.basis + (result.capacity.basis === 1 ? ' verbrauchten Rolle' : ' verbrauchten Rollen')
      : 'geschätzt') + ')'],
    ['Abschnitt', '1 Zoll (25,4 mm); ein Ticket belegt meist 2–3 Abschnitte, aufeinanderfolgende Tickets teilen sich den Grenzabschnitt'],
    ['Druck ohne Verkauf', 'Testdruck, Abrechnung, Fahrplan, Dienstplan oder Dokument aus der Drucker-App; belegt Abschnitte wie ein Verkauf'],
    ['Lücke', 'Abschnitt zwischen kleinstem und größtem belegten Abschnitt einer Rolle ohne Verkauf und ohne Druck; erklärt, wenn auf dem Drucker dazwischen das Papier beim Einlegen oder Verbinden ohne Druck vorgeschoben wurde (gemeldete Stelle hinter dem letzten Druck), ein Verkauf oder Druck ohne erkannte Rollennummer lag oder der Drucker neu verbunden bzw. Papier eingelegt wurde, ohne die Stelle zu lesen; unkritisch bei genau einem Abschnitt (daraus lässt sich kein Ticket drucken)'],
    ['Eingelegt', 'Stelle der Rolle beim Einlegen (Papiereinzug) bzw. beim Verbinden des Druckers, wie sie die Drucker-App gemeldet hat']
  ].forEach((row) => info.addRow(row));
  info.getColumn(1).font = { bold: true };

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Rollennachweis_' + moment(now).format('YYYY-MM-DD') + '.xlsx');
}
